import { ButtonHTMLAttributes, ReactNode } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  isLoading?: boolean;
}

export const Button = ({ 
  variant = 'primary', 
  size = 'md', 
  children, 
  className, 
  isLoading, 
  disabled,
  ...props 
}: ButtonProps) => {
  
  const variants = {
    primary: "gold-gradient text-white shadow-lg shadow-gold-primary/20 hover:scale-[1.03] active:scale-95",
    outline: "bg-transparent border-2 border-slate-200 text-slate-700 hover:border-gold-primary hover:text-gold-primary",
    ghost: "bg-white text-slate-600 border border-slate-100 hover:bg-slate-50",
    secondary: "bg-slate-900 text-white hover:bg-slate-800"
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-8 py-4 font-bold",
    lg: "px-10 py-5 text-lg font-extrabold"
  };

  return (
    <button 
      className={cn(
        "relative inline-flex items-center justify-center rounded-full transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="opacity-0">{children}</span>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="animate-spin h-5 w-5 text-current" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </>
      ) : (
        children
      )}
    </button>
  );
};